

import { useEffect, useRef, useState } from "react";
import { CompanionsEntry } from "./CompanionEntry";
import { PanelEmptyState } from "./PanelEmptyState";
import { useTranslations } from "next-intl";
import { useGuestCompanions } from "@/providers/GuestCompanions";
import { LoadingSpinner } from "@/components/LoadingSpinner/LoadingSpinner";
import { IconPlus } from "@tabler/icons-react";
import { Guest } from "@/types/Guest";
import { useRole } from "@/providers/RoleProvider";
import { DropDown } from "@/components/DropDown/DropDown";
import { COMPANION_SCOPE, type CompanionScope } from "@/types/enums/CompanionScope";
import { getPref, setPref } from "@/lib/storage/userPrefs";

type CompanionsListProps = {
  guestId: string;
  setOpeningId: React.Dispatch<React.SetStateAction<string | Guest>>;
  /** See `GuestNotes`. An archived host has an archived party. */
  hostArchived?: boolean;
};

/** Order shown in the dropdown: narrowest first, so "more rows" reads downwards. */
const VIEW_ITEMS: CompanionScope[] = [
  COMPANION_SCOPE.PARTY,
  COMPANION_SCOPE.PARTY_AND_REQUESTS,
  COMPANION_SCOPE.ALL,
];

/**
 * Remembered per user, not per event: an organiser who wants to see the
 * removed rows wants that everywhere, and a per-event key would make the
 * setting look broken on the next event. Same store the table uses for sort
 * and page size.
 */
const VIEW_PREF_KEY = "guestDetail.companionScope";

function storedScope(): CompanionScope {
  try {
    const raw = getPref(VIEW_PREF_KEY);
    if (VIEW_ITEMS.includes(raw as CompanionScope)) return raw as CompanionScope;
  } catch {
    // A blocked storage read must not cost the panel its default view.
  }
  // The party PLUS the open requests: that is what this panel showed before it
  // could be switched, and an undecided request is the one row that asks the
  // organiser to do something. Hiding it by default would bury work.
  return COMPANION_SCOPE.PARTY_AND_REQUESTS;
}

export const CompanionsList: React.FC<CompanionsListProps> = ({
  guestId,
  setOpeningId,
  hostArchived = false,
}) => {
  const { legacyRoleFloor, role } = useRole();
  const t = useTranslations("companion");
  const t3 = useTranslations("videoMessages");
  // The "+" reuses the create dialog's label, exactly like the guest-detail
  // header's own "+" does (GuestDetailBody).
  const tCreate = useTranslations("popup-create-guest");
  const { allCompanions, loading, errorMessage, fetchCompanions } =
    useGuestCompanions();

  const [chosenScope, setChosenScope] = useState<CompanionScope>(storedScope);

  /**
   * An archived host has an archived party, and both party scopes filter
   * `archived: false` — so asking for one would show "no companions" for a
   * host that has five, and hand every reader of this list a zero. The backend
   * says so in its own words at `GuestRepository.activeCompanionPredicate`'s
   * sibling: *"that hard-codes `archived:false`, which would empty every party
   * in the ARCHIVED view."*
   *
   * There is nothing to choose here either: every row is archived, so the
   * dropdown is hidden rather than left offering two empty views.
   */
  const scope = hostArchived ? COMPANION_SCOPE.ALL : chosenScope;

  /**
   * Which scope THIS panel last asked for.
   *
   * The provider caches by host id alone, so only the panel knows whether the
   * cached list still answers the question now being asked. A switch has to
   * force a refetch (`fetchCompanions` returns the cached set otherwise and
   * the switch looks dead); a plain re-render must NOT, because
   * `fetchCompanions`' identity changes on every companion-state update and an
   * unconditional refetch here would fetch itself in a loop.
   */
  const lastScopeRef = useRef<CompanionScope | null>(null);

  useEffect(() => {
    const scopeChanged =
      lastScopeRef.current !== null && lastScopeRef.current !== scope;
    lastScopeRef.current = scope;
    void fetchCompanions(guestId, scopeChanged, scope);
  }, [guestId, scope, fetchCompanions]);

  const companions = allCompanions.get(guestId);

  // No `justify-between` on the column: with the scope dropdown on top and
  // the "+" at the bottom it centred the list in the leftover height, so one
  // row floated mid-panel over a blank half-screen. The list takes the room
  // (`flex-1`), the "+" pins itself to the bottom (`mt-auto`).
  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {!hostArchived && (
      <div className="flex flex-row items-center justify-end gap-2 pt-1 pb-2">
        <span className="body-4-regular font-tertiary">{t("viewLabel")}</span>
        <DropDown
          border={true}
          height="h-8"
          fontFocus="body-4-regular"
          translate={true}
          translation={(item) => t(`view_${item}`)}
          translationSelected={(item) => t(`view_${item}`)}
          initialItem={scope}
          allItems={VIEW_ITEMS}
          testId="companion-scope"
          itemTestId={(item) => `companion-scope-${item}`}
          onDropdownAction={(item) => {
            const next = item as CompanionScope;
            setChosenScope(next);
            try {
              setPref(VIEW_PREF_KEY, next);
            } catch {
              // Remembering the choice is a convenience; losing it is not a
              // reason to leave the panel on the old view.
            }
          }}
        />
      </div>
      )}
      {loading ? (
        <LoadingSpinner />
      ) : errorMessage ? (
        <div className="p-4 text-center font-error">{errorMessage}</div>
      ) : !companions || companions.length === 0 ? (
        <PanelEmptyState riv="NoCompanion" title={t3("noCompanions")} />
      ) : (
        <div className="flex-1">
          {companions.map((companion) => (
            <CompanionsEntry
              setOpeningId={setOpeningId}
              key={`${companion.firstName ?? ""}${companion.lastName ?? ""}${
                companion.guestId ?? ""
              }`}
              companion={companion}
              hostGuestId={guestId}
            />
          ))}
        </div>
      )}
      {legacyRoleFloor !== "VIEWER" && (
        <div className="w-full flex justify-end items-end mt-auto pt-2 mb-2">
          {/* Icon-only, so it carried no accessible name at all: a screen
              reader announced "button", and an e2e selector had to reach for
              the svg class. Named from the create dialog's own label — same
              affordance, same word. */}
          <button
            type="button"
            data-testid="companion-add"
            aria-label={tCreate("createGuest")}
            title={tCreate("createGuest")}
            onClick={() => setOpeningId("create")}
            className="rounded-full p-3 border outline-tertiary"
          >
            <IconPlus size={20} className="font-secondary" />
          </button>
        </div>
      )}
    </div>
  );
};
